import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * "Ver como socio" del Portal de Socios (sólo Super Admin).
 *
 * El socio impersonado se identifica por su email: `socio_mis_proyectos(p_email_socio)`
 * devuelve SUS proyectos (la RPC rechaza con 42501 a quien no sea Super Admin).
 * Se persiste en sessionStorage para sobrevivir al remount del layout entre rutas
 * y a un F5 dentro de la pestaña; si el storage falla, vive sólo en memoria.
 *
 * Este archivo NO importa de `@/hooks/usePortalSocios` a propósito: los hooks del
 * portal leen `useSocioViewAsEmail()` para meter el email en sus queryKeys.
 */
export interface SocioImpersonationTarget {
  id_persona: number;
  nombre: string;
  /** Email con el que se consulta `socio_mis_proyectos`: el de la persona (email ?? email_usuario). */
  email: string;
}

interface SocioImpersonationValue {
  canImpersonate: boolean;
  target: SocioImpersonationTarget | null;
  isImpersonating: boolean;
  setTarget: (t: SocioImpersonationTarget | null) => void;
  clear: () => void;
}

const NOOP: SocioImpersonationValue = {
  canImpersonate: false,
  target: null,
  isImpersonating: false,
  setTarget: () => {},
  clear: () => {},
};

const SocioImpersonationContext = createContext<SocioImpersonationValue>(NOOP);

const storageKey = (uid: string | null | undefined) => `sozu.portal-socios.ver-como.${uid ?? "anon"}`;

function leer(key: string): SocioImpersonationTarget | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (v && typeof v.email === "string" && v.email) {
      return { id_persona: Number(v.id_persona) || 0, nombre: String(v.nombre ?? v.email), email: v.email };
    }
    return null;
  } catch {
    return null;
  }
}

function escribir(key: string, t: SocioImpersonationTarget | null) {
  try {
    if (t) window.sessionStorage.setItem(key, JSON.stringify(t));
    else window.sessionStorage.removeItem(key);
  } catch {
    /* storage bloqueado: la impersonación vive sólo en memoria */
  }
}

export function SocioImpersonationProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const canImpersonate = profile?.rol_nombre === "Super Administrador";
  const key = storageKey(user?.id);
  const [stored, setStored] = useState<SocioImpersonationTarget | null>(() => leer(key));

  useEffect(() => {
    setStored(leer(key));
  }, [key]);

  const setTarget = useCallback(
    (t: SocioImpersonationTarget | null) => {
      setStored(t);
      escribir(key, t);
    },
    [key],
  );
  const clear = useCallback(() => setTarget(null), [setTarget]);

  // Si el perfil no es Super Admin, se ignora cualquier target guardado.
  const target = canImpersonate ? stored : null;

  const value = useMemo<SocioImpersonationValue>(
    () => ({ canImpersonate, target, isImpersonating: !!target, setTarget, clear }),
    [canImpersonate, target, setTarget, clear],
  );

  return <SocioImpersonationContext.Provider value={value}>{children}</SocioImpersonationContext.Provider>;
}

export function useSocioImpersonation(): SocioImpersonationValue {
  return useContext(SocioImpersonationContext);
}

/** Email del socio impersonado (o null). Seguro fuera del provider. */
export function useSocioViewAsEmail(): string | null {
  return useContext(SocioImpersonationContext).target?.email ?? null;
}
